import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useQuery } from '@apollo/react-hooks';
import { connect } from 'react-redux';
import { change, Field, FieldArray, reduxForm } from 'redux-form';

import { RENTAL_ITEMS_AVAILABLE_UNITS } from 'graphql/queries/rental-items';

import { getListOfAvailableCurrencies } from 'utils/book-accommodation-and-activity/currency';
import { getAvailableUnits } from 'utils/book-rental-items/prepareAvailableUnits';
import { getDisabledDates, getDisabledDatesWithTime } from 'utils/book-rental-items/datesAvailability';
import {
  getMaxUnits,
  getMaxUnitsRentalItemsSeveralDays,
  getMaxUnitsRentalItemsHourly
} from 'utils/book-rental-items/maxAvailableUnits';
import { getReceivedRentalItemsBookingsDates } from 'utils/rental-items/prepareReceivedRentalItemsData';
import { MultiDatePickerCalendar, SingleDatePicker, CustomTimePicker } from 'components';

import BookingInformation from '../booking-information/BookingInformation';
import RentalVisitors from '../visiters/RentalVisiter';

import useStyles from '../date-and-guests/styles';
import validate from 'pages/book-rental-items/validate';
import WidgetLoader from '../widget-loader/WidgetLoader';

const initialValues = {
  startBookingsDate: moment(),
  dates: null,
  startingDate: null,
  multiDates: null,
  availableUnits: [],
  currency: null,
  availableCurrencies: [],
  maxAvailableUnits: 0,
  addOns: [],
  languages: [],
  startTime: null,
  endTime: null,
  minMaxHours: null,
  minDays: null,
  maxDays : null,
  selectedLanguage: 'English'
};

const PricesTypes = [
  'pricesFixedForm',
  'pricesDailyForm',
  'pricesWeeklyForm'
];

const DatesAndUnits = ({
  initialSelectedCurrency,
  initialSelectedLanguage,
  productData,
  receivedProduct,
  changeFormFieldValue,
  widgetForm,
  handleSubmit,
  ownerId,
  languages,
}) => {  


  const classes = useStyles();
  const productId = productData.generalForm.id;
  const priceTypes = productData.pricesType;

  const multiDates = widgetForm.values.multiDates;
  const startingDate = widgetForm.values.startingDate;
  const startTime = widgetForm.values.startTime;
  const endTime = widgetForm.values.endTime;

  const [calendarFirstDate, setCalendarFirstDate] = useState(null);
  const [availableUnitsData, setAvailableUnitsData] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const bottomRef = useRef(null);

  const {
    loading: availableUnitsLoading,
    data: receivedAvailableUnitsData,
    refetch: refetchAvailableUnitsData
  } = useQuery(RENTAL_ITEMS_AVAILABLE_UNITS, {
    skip: !calendarFirstDate,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      businessOwnerId: ownerId,
      rentalItemsId: productId,
      startPeriod: moment(calendarFirstDate)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(calendarFirstDate).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    }
  });

  useEffect(() => {
    if (!receivedAvailableUnitsData) return;

    const availableUnitsData = getReceivedRentalItemsBookingsDates(
      receivedAvailableUnitsData
    );

    setAvailableUnitsData(availableUnitsData);
  }, [receivedAvailableUnitsData]);

  useEffect(() => {
    if (!receivedProduct) return;
    setListOfAvailableCurrencies(receivedProduct);
  }, [receivedProduct]);

  useEffect(() => {
    if (!widgetForm.values.languages.length) {
      changeFormFieldValue('languages', languages);
      changeFormFieldValue('selectedLanguage', initialSelectedLanguage);
    }

      if (productData) {
        changeFormFieldValue('pricesType', productData.pricesType);

        if (productData?.minMaxHours) {
          changeFormFieldValue(
            'minMaxHours',
            productData.minMaxHours
          );
        }

        changeFormFieldValue('minDays', productData.minDays);

        changeFormFieldValue('maxDays', productData.maxDays);
      }

    setCalendarFirstDate(
      widgetForm.values.startingDate || widgetForm.values.multiDates?.startDate || moment()
    );
    
    const maxAvailableUnits = productData['generalForm'].availableUnits;
    if (maxAvailableUnits) {
      setMaxAvailableUnits(maxAvailableUnits);
    }
  }, []);

  useEffect(() => {
    if (!widgetForm.values.multiDates) return;
    
    let { startDate, endDate } = widgetForm.values.multiDates;
    if (!endDate) {
      endDate = startDate;
    }
    
    const isSame = moment(startDate).isSame(endDate, 'day');
    if (isSame) {
      changeFormFieldValue('dates', null);
      changeFormFieldValue('startingDate', startDate);
    } else {
      changeFormFieldValue('startingDate', null);
      changeFormFieldValue('dates', widgetForm.values.multiDates);
    }
  }, [widgetForm.values.multiDates]);

  useEffect(() => {
    const startTimeMoment = moment.isMoment(startTime) ? startTime : (startTime ? moment(startTime) : null);
    const endTimeMoment = moment.isMoment(endTime) ? endTime : (endTime ? moment(endTime) : null);
    const hasValidTimes = startTimeMoment && endTimeMoment && startTimeMoment.isValid() && endTimeMoment.isValid();

    try {
      if (!productData) return;

     
      if (multiDates?.startDate && multiDates?.endDate && priceTypes !== 'pricesHourlyForm') {
        handleMultiDateAvailableSeats(productData);
        setUnits(productData);
      }
      
      else if (priceTypes === 'pricesHourlyForm') {
        if (startingDate && hasValidTimes) {
          if (endTimeMoment.isBefore(startTimeMoment) && endTimeMoment.diff(startTimeMoment, 'hours') < -12) {
          
            return;
          }

          handleHourlyAvailableSeats(productData);
          setUnits(productData);
        } else {
          setUnits(productData);
        }
      }
  
      else if (startingDate) {
        handleMultiDateAvailableSeats(productData);
        setUnits(productData);
      }
      else {
        setUnits(productData);
      }

    } catch (error) {
      
      setUnits(productData);
    }
  }, [
    productData,
    availableUnitsData,
    widgetForm.values.multiDates,
    widgetForm.values.startingDate,
    widgetForm.values.startTime,
    widgetForm.values.endTime,
    widgetForm.values.currency,
    priceTypes
  ]);

  useEffect(() => {
    if (
      (widgetForm.values.dates?.startDate && widgetForm.values.dates?.endDate) ||
      (widgetForm.values.multiDates?.startDate && widgetForm.values.multiDates?.endDate)
    ) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    }
  }, [widgetForm.values.dates, widgetForm.values.multiDates]);

  useEffect(() => {
    if (
      widgetForm?.submitFailed &&
      widgetForm?.syncErrors?.availableUnits?._error
    ) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    }
  }, [
    widgetForm?.syncErrors?.availableUnits?._error,
    widgetForm?.submitFailed
  ]);

  useLayoutEffect(() => {
    const updateSize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const setListOfAvailableCurrencies = receivedProduct => {
    const listOfAvailableCurrencies = getListOfAvailableCurrencies(
     Array.isArray(receivedProduct.prices) && receivedProduct.prices.length > 0
        ? receivedProduct.prices
        : receivedProduct.hourlyPrices
    );  

    changeFormFieldValue('availableCurrencies', listOfAvailableCurrencies);
    changeFormFieldValue('currency', initialSelectedCurrency);
  };

  const setMaxAvailableUnits = seatsLeft => {
    const maxUnits = getMaxUnits(
      seatsLeft,
      null,
      widgetForm,
      availableUnitsData
    );

    changeFormFieldValue('maxAvailableUnits', maxUnits);
  };

  const handleMultiDateAvailableSeats = rentalItem => {
    if (!rentalItem) return;

    let startDate, endDate;

    if (multiDates?.startDate && multiDates?.endDate) {
      startDate = multiDates.startDate;
      endDate = multiDates.endDate;
    } else if (startingDate) {
      startDate = startingDate;
      endDate = startingDate;
    } else {
      return;
    }

    const availableUnitsPerDate = availableUnitsData
      ? availableUnitsData.rentalItems?.[0]?.availableUnitsPerDate ?? []
      : [];

    const availableUnits = getMaxUnitsRentalItemsSeveralDays(
      availableUnitsPerDate,
      startDate,
      endDate,
      null
    );

    if (availableUnits !== null && availableUnits !== undefined) {
      setMaxAvailableUnits(availableUnits);
    } else {
      setMaxAvailableUnits(rentalItem.generalForm.availableUnits);
    }
  };

  const handleHourlyAvailableSeats = (rentalItems) => {
    const formValues = widgetForm?.values;
    
    if (!rentalItems || !formValues) {
     
      return;
    }

    const startDate = formValues?.startingDate;

    
    const endDate = formValues?.multiDates?.endDate || startDate;
    const startTime = formValues?.startTime;
    const endTime = formValues?.endTime;

    if (!startDate || !startTime || !endTime) {
     
      return;
    }

    const availableUnitsPerDate = 
      availableUnitsData?.rentalItems?.[0]?.availableUnitsPerDate ?? [];

    const availableUnits = getMaxUnitsRentalItemsHourly(
      availableUnitsPerDate,
      startDate,
      endDate,
      null, 
      startTime,
      endTime,
      false 
    );

    if (availableUnits !== null && availableUnits !== undefined) {
      setMaxAvailableUnits(availableUnits);
    } else {
      const fallback = rentalItems?.generalForm?.availableUnits ?? 0;
      setMaxAvailableUnits(fallback);
    }
  };

  const setUnits = rentalItems => {
    const currency = widgetForm.values.currency;
    const createdUnits = widgetForm.values.availableUnits;

    const units = getAvailableUnits(
      rentalItems,
      widgetForm,
      currency,
      createdUnits
    );

    changeFormFieldValue('availableUnits', units);
  };

  const changeMonthHandler = date => {
    setCalendarFirstDate(date);
    
    refetchAvailableUnitsData({
      businessOwnerId: ownerId,
      rentalItemsId: productId,
      startPeriod: moment(date)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(date).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    });
  };

  return (
    <>
      <WidgetLoader loading={availableUnitsLoading} />

      <div className={classes.wrapper}>
        <div className={classes.leftSide}>
          <form onSubmit={handleSubmit}>
            {PricesTypes.includes(priceTypes) ? (
              <Field
                name="multiDates"
                label="Select date *"
                numberOfMonths={
                  windowWidth > 1010 ? 2 : windowWidth <= 750 ? 1 : 2
                }
                daySize={windowWidth > 600 ? 38 : 41}
                initialFocus="startDate"
                dateRangePickerClass="dateRangeOpenedPicker"
                inputClassName={classes.disableInputFocus}
                component={MultiDatePickerCalendar}
                changeMonthsCallback={changeMonthHandler}
                disabledDates={getDisabledDates(
                  availableUnitsData,
                  null,
                  productData
                )}
                labelTypographyVariant={'h3'}
                labelClass={classes.label}
              />
            ) : (
              <Field
                name="startingDate"
                label="Starting date *"
                numberOfMonths={
                  windowWidth > 1010 ? 2 : windowWidth <= 750 ? 1 : 2
                }
                daySize={windowWidth > 600 ? 38 : 41}
                component={SingleDatePicker}
                changeMonthsCallback={changeMonthHandler}
                disabledDates={getDisabledDatesWithTime(
                  availableUnitsData,
                  null
                )}
              />
            )}

            {!PricesTypes.includes(priceTypes) && (
              <div className={classes.timePickerRow}>
                <Field
                  name="startTime"
                  label="Starting time *"
                  component={CustomTimePicker}
                  selectedDate={widgetForm.values.startingDate}
                  startTimeValue={widgetForm.values.startTime}
                  availableUnitsData={availableUnitsData?.rentalItems}
                  className={classes.timeInput}
                />

                <Field
                  name="endTime"
                  label="Ending time *"
                  component={CustomTimePicker}
                  selectedDate={widgetForm.values.startingDate}
                  startTimeValue={widgetForm.values.endTime}
                  availableUnitsData={availableUnitsData?.rentalItems}
                  className={classes.timeInput}
                />
              </div>
            )}
            
            <FieldArray
              name="availableUnits"
              title="Select number of units"
              component={RentalVisitors}
               props={{ 
                widgetForm,
                debug: true
              }}
              titleClass={classes.label}
              itemClass={classes.participant}
              currency={widgetForm.values.currency ?? 'NOK'}
            />
            
            <div ref={bottomRef} />
            <button hidden={true} type={'submit'}>
              Submit
            </button>
          </form>
        </div>

        <div className={classes.rightSide}>
          <BookingInformation data={productData} form={widgetForm} />
        </div>
      </div>
    </>
  );
};

const mapStateToProps = state => {
  return {
    widgetForm: state.form.widgetForm
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (field, value) => {
    dispatch(change('widgetForm', field, value));
  }
});

export default reduxForm({
  form: 'widgetForm',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  touchOnBlur: false,
  validate: (values, props) => validate(values, props),
  initialValues
})(connect(mapStateToProps, mapDispatchToProps)(DatesAndUnits));